"use client";
import { useEffect, useState } from "react";
import Link from "next/link";
import { ArrowUpRight, Plus } from "lucide-react";
import { api, useApp } from "./provider";
import { ManagedVideo } from "./managed-video";
import { loadAslDictionary } from "../../packages/api-client/asl";
import type { AslDictionaryResult } from "@/lib/types";
import { AslThumbnail } from "./asl-thumbnail";

export function AslSearchVideo({ query }: { query: string }) {
  const { t, user } = useApp();
  const term = query.trim();
  const [result, setResult] = useState<AslDictionaryResult | null>(null);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState("");
  const [retry, setRetry] = useState(0);
  const [failedVideo, setFailedVideo] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    setResult(null);
    setSelectedId("");
    setFailure("");
    setLoading(true);
    const timer = setTimeout(() => {
      loadAslDictionary(api, term, controller.signal)
        .then((value) => {
          if (!controller.signal.aborted) setResult(value);
        })
        .catch((error) => {
          if (!controller.signal.aborted)
            setFailure(error instanceof Error ? error.message : "unavailable");
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 400);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [term, retry, user?.id]);
  const selected =
    result?.variations.find((item) => item.id === selectedId) ||
    result?.variations[0];
  const selectedIndex =
    result?.variations.findIndex((item) => item.id === selected?.id) ?? 0;
  return (
    <section
      className="asl-dictionary-result"
      aria-label={t("ASL meanings and sign variations", "ASL词义与手语变体")}
      aria-busy={loading}
    >
      <div className="asl-result-heading">
        <div className="asl-result-title">
          <div className="eyebrow">{t("ASL DICTIONARY", "ASL词典")}</div>
          <h3>{result?.word || term}</h3>
        </div>
        <Link href="/upload" className="asl-add-sign">
          <span className="asl-add-sign-icon"><Plus size={18} aria-hidden="true" /></span>
          <span>{t("Add Your Sign", "添加你的手语")}</span>
          <ArrowUpRight size={17} aria-hidden="true" />
        </Link>
      </div>
      <p role="status">
        {loading
          ? t(
              "Loading meanings and sign variations… The first search may take a moment.",
              "正在加载词义与手语变体…首次搜索可能需要一些时间。",
            )
          : result
            ? t(
                `${result.variations.length} sign variations. Select a variation to explore its meaning and video.`,
                `${result.variations.length}种手语变体。选择变体查看词义和视频。`,
              )
            : failure === "not_found"
              ? t(
                  "No ASL meanings or sign variations were found for this word.",
                  "未找到此词的ASL词义或手语变体。",
                )
              : failure === "rate_limit"
                ? t(
                    "Search limit reached. Please try again later.",
                    "已达到搜索限额，请稍后重试。",
                  )
                : failure === "asl_authentication"
                  ? t(
                      "The ASL API could not authenticate. Check the server API key and retry.",
                      "ASL API认证失败，请检查服务器API密钥后重试。",
                    )
                  : t(
                      "We couldn’t load the ASL dictionary. Please try again.",
                      "无法加载ASL词典，请重试。",
                    )}
      </p>
      {!loading && !result && failure !== "not_found" && (
        <div role="alert">
          <button
            className="button"
            onClick={() => setRetry((value) => value + 1)}
          >
            {t("Retry", "重试")}
          </button>
        </div>
      )}
      {selected && result && (
        <div className="asl-variation-layout">
          <div
            className="asl-variation-list"
            role="group"
            aria-label={t("Choose an ASL variation", "选择ASL变体")}
          >
            {result.variations.map((item, index) => (
              <button
                key={item.id}
                type="button"
                aria-pressed={item.id === selected.id}
                className="asl-variation-option"
                onClick={() => setSelectedId(item.id)}
              >
                <AslThumbnail key={item.id} src={item.thumbnail_url} label={t(`${result.word} · Variation ${index + 1}`, `${result.word} · 变体${index + 1}`)} className="asl-variation-thumbnail" />
                <strong>
                  {t(`Variation ${index + 1}`, `变体${index + 1}`)}
                </strong>
                <span>
                  {item.meaning || t("Meaning not provided.", "未提供词义。")}
                </span>
                <small>Voisea · ASL</small>
              </button>
            ))}
          </div>
          <div
            className="asl-selected-variation"
            onErrorCapture={() => setFailedVideo(selected.id)}
            onPlayingCapture={() => setFailedVideo("")}
          >
            <h4>
              {t(`Variation ${selectedIndex + 1}`, `变体${selectedIndex + 1}`)}
            </h4>
            <p>
              {selected.meaning || t("Meaning not provided.", "未提供词义。")}
            </p>
            <ManagedVideo
              key={`${selected.id}:${retry}`}
              src={selected.url}
              poster={selected.thumbnail_url}
              videoId={selected.video_id}
              className="sample-video"
              label={t(
                `${result.word}, ASL variation ${selectedIndex + 1}`,
                `${result.word}，ASL变体${selectedIndex + 1}`,
              )}
            />
            {failedVideo === selected.id && (
              <p role="alert">
                {t(
                  "This API video is unavailable. Retry the video or choose another variation.",
                  "此API视频暂时不可用，请重试或选择其他变体。",
                )}
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
